/**
 * Слой электрики: приборы на стенах (розетки, выключатели, распредкоробки, бра)
 * и светильники на потолке (люстры, споты). Оба вида — в одном сторе, потому что
 * это один слой чертежа с одной видимостью, и кабель ведут между любыми из них.
 */
import { create } from "zustand";
import {
	fixturePlanPoint,
	rerouteConnectedCables,
	uid,
	wallLength,
	type CatalogRef,
	type Fixture,
	type FloorItem,
} from "@/shared/plan";
import { useWallsStore } from "./wallsStore";
import { useWiringStore } from "./wiringStore";

/** Держит объект в границах стены: вдоль — по длине, по высоте — до потолка. */
const clampFixture = (fixture: Fixture, length: number, height: number): Fixture => ({
	...fixture,
	offset: Math.max(0, Math.min(length, fixture.offset)),
	elevation: Math.max(0, Math.min(height, fixture.elevation)),
});

interface FixturesLayerState {
	/** Приборы на стенах */
	items: Record<string, Fixture>;
	/** Светильники на потолке — точка плана, под потолок их поднимают движки */
	ceilingItems: Record<string, FloorItem>;
	visible: boolean;

	addCeilingItem: (ref: CatalogRef, x: number, y: number) => string;
	updateCeilingItem: (id: string, patch: Partial<Omit<FloorItem, "id">>) => void;
	setCeilingItemPosition: (id: string, x: number, y: number) => void;
	restoreCeilingItems: (items: Record<string, FloorItem>) => void;

	addFixture: (
		wallId: string,
		ref: CatalogRef,
		offset: number,
		elevation: number,
		side: 1 | -1,
	) => string | null;
	updateFixture: (id: string, patch: Partial<Omit<Fixture, "id" | "wallId">>) => void;
	setFixturePlacement: (id: string, offset: number, elevation: number) => void;
	setFixtureWall: (id: string, wallId: string, offset: number, side: 1 | -1) => void;

	setVisible: (visible: boolean) => void;
	toggleVisible: () => void;

	restoreFixtures: (fixtures: Record<string, Fixture>) => void;
}

const updateWithRerouting = (id: string, update: () => void) => {
	const graph = useWallsStore.getState().items;
	const pointOf = (state: FixturesLayerState, endpointId: string) => {
		const fixture = state.items[endpointId];
		if (fixture) return fixturePlanPoint(graph, fixture);
		const item = state.ceilingItems[endpointId];
		return item ? { x: item.x, y: item.y } : null;
	};
	const previous = pointOf(useFixturesStore.getState(), id);
	update();
	const state = useFixturesStore.getState();
	const current = pointOf(state, id);
	if (!previous || !current || (previous.x === current.x && previous.y === current.y)) return;
	const wiring = useWiringStore.getState();
	const cables = rerouteConnectedCables(wiring.items, id, previous, (endpointId) =>
		pointOf(state, endpointId),
	);
	if (cables !== wiring.items) wiring.restoreCables(cables);
};

export const useFixturesStore = create<FixturesLayerState>((set) => ({
	items: {},
	ceilingItems: {},
	visible: true,

	addCeilingItem: (ref, x, y) => {
		const id = uid("l");
		const item: FloorItem = { id, ref, x: Math.round(x), y: Math.round(y), rotation: 0 };
		set((s) => ({
			ceilingItems: { ...s.ceilingItems, [id]: item },
			visible: true,
		}));
		return id;
	},

	updateCeilingItem: (id, patch) =>
		updateWithRerouting(id, () =>
			set((s) => {
				const item = s.ceilingItems[id];
				if (!item) return s;
				const next: FloorItem = { ...item, ...patch };
				next.rotation = ((next.rotation % 360) + 360) % 360;
				return { ceilingItems: { ...s.ceilingItems, [id]: next } };
			}),
		),

	setCeilingItemPosition: (id, x, y) =>
		updateWithRerouting(id, () =>
			set((s) => {
				const item = s.ceilingItems[id];
				if (!item) return s;
				return {
					ceilingItems: {
						...s.ceilingItems,
						[id]: { ...item, x: Math.round(x), y: Math.round(y) },
					},
				};
			}),
		),

	restoreCeilingItems: (items) => set({ ceilingItems: items }),

	addFixture: (wallId, ref, offset, elevation, side) => {
		const wallsState = useWallsStore.getState();
		const wall = wallsState.items.walls[wallId];
		if (!wall) return null;
		const length = wallLength(wallsState.items, wall);
		if (length < 100) return null;

		const id = uid("f");
		const fixture = clampFixture({ id, wallId, ref, offset, elevation, side }, length, wall.height);
		set((s) => ({
			items: { ...s.items, [id]: fixture },
			visible: true,
		}));
		return id;
	},

	updateFixture: (id, patch) =>
		updateWithRerouting(id, () =>
			set((s) => {
				const fixture = s.items[id];
				if (!fixture) return s;

				const wallsState = useWallsStore.getState();
				const wall = wallsState.items.walls[fixture.wallId];
				if (!wall) return s;

				const next = clampFixture(
					{ ...fixture, ...patch },
					wallLength(wallsState.items, wall),
					wall.height,
				);
				return { items: { ...s.items, [id]: next } };
			}),
		),

	setFixturePlacement: (id, offset, elevation) =>
		updateWithRerouting(id, () =>
			set((s) => {
				const fixture = s.items[id];
				if (!fixture) return s;

				const wallsState = useWallsStore.getState();
				const wall = wallsState.items.walls[fixture.wallId];
				if (!wall) return s;

				const next = clampFixture(
					{ ...fixture, offset, elevation },
					wallLength(wallsState.items, wall),
					wall.height,
				);
				return { items: { ...s.items, [id]: next } };
			}),
		),

	setFixtureWall: (id, wallId, offset, side) =>
		updateWithRerouting(id, () =>
			set((s) => {
				const fixture = s.items[id];
				if (!fixture) return s;

				const wallsState = useWallsStore.getState();
				const wall = wallsState.items.walls[wallId];
				if (!wall) return s;

				const next = clampFixture(
					{ ...fixture, wallId, offset, side },
					wallLength(wallsState.items, wall),
					wall.height,
				);
				return { items: { ...s.items, [id]: next } };
			}),
		),

	setVisible: (visible) => set({ visible }),
	toggleVisible: () => set((s) => ({ visible: !s.visible })),

	restoreFixtures: (fixtures) => set({ items: fixtures }),
}));
